// German public holidays, computed (Easter via the Gauss/Meeus algorithm), no data files.
// National holidays plus the state-specific ones; `NI` (Lower Saxony) is the default state.
import { addDaysIso, type IsoDate } from "./time.js";

export const FEDERAL_STATES = [
  "BW",
  "BY",
  "BE",
  "BB",
  "HB",
  "HH",
  "HE",
  "MV",
  "NI",
  "NW",
  "RP",
  "SL",
  "SN",
  "ST",
  "SH",
  "TH",
] as const;
export type FederalState = (typeof FEDERAL_STATES)[number];

export type Holiday = { date: IsoDate; name: string };

// Anonymous Gregorian algorithm (Meeus/Jones/Butcher).
export function easterSunday(year: number): IsoDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const pad = (n: number) => String(n).padStart(2, "0");

// Names are German by nature (they are displayed as-is and match zvoove's calendar wording).
export function holidaysForYear(year: number, state: FederalState = "NI"): Holiday[] {
  const easter = easterSunday(year);
  const fixed = (month: number, day: number, name: string): Holiday => ({
    date: `${year}-${pad(month)}-${pad(day)}`,
    name,
  });
  const moved = (offset: number, name: string): Holiday => ({
    date: addDaysIso(easter, offset),
    name,
  });

  const list: Holiday[] = [
    fixed(1, 1, "Neujahr"),
    moved(-2, "Karfreitag"),
    moved(1, "Ostermontag"),
    fixed(5, 1, "Tag der Arbeit"),
    moved(39, "Christi Himmelfahrt"),
    moved(50, "Pfingstmontag"),
    fixed(10, 3, "Tag der Deutschen Einheit"),
    fixed(12, 25, "1. Weihnachtstag"),
    fixed(12, 26, "2. Weihnachtstag"),
  ];

  const epiphany = fixed(1, 6, "Heilige Drei Könige");
  const corpusChristi = moved(60, "Fronleichnam");
  const assumption = fixed(8, 15, "Mariä Himmelfahrt");
  const reformation = fixed(10, 31, "Reformationstag");
  const allSaints = fixed(11, 1, "Allerheiligen");
  const womensDay = fixed(3, 8, "Internationaler Frauentag");
  const childrensDay = fixed(9, 20, "Weltkindertag");
  const repentance = (): Holiday => {
    // Wednesday before 23 November.
    const nov23 = new Date(Date.UTC(year, 10, 23));
    const back = (nov23.getUTCDay() - 3 + 7) % 7 || 7;
    return { date: addDaysIso(`${year}-11-23`, -back), name: "Buß- und Bettag" };
  };

  const byState: Record<FederalState, Holiday[]> = {
    BW: [epiphany, corpusChristi, allSaints],
    BY: [epiphany, corpusChristi, assumption, allSaints],
    BE: [womensDay],
    BB: [reformation],
    HB: [reformation],
    HH: [reformation],
    HE: [corpusChristi],
    MV: [womensDay, reformation],
    NI: [reformation],
    NW: [corpusChristi, allSaints],
    RP: [corpusChristi, allSaints],
    SL: [corpusChristi, assumption, allSaints],
    SN: [reformation, repentance()],
    ST: [epiphany, reformation],
    SH: [reformation],
    TH: [childrensDay, reformation],
  };

  return [...list, ...byState[state]].sort((a, b) => a.date.localeCompare(b.date));
}

// Map date → name for a range of years (cheap; a few dozen entries).
export function holidayMap(years: number[], state: FederalState = "NI"): Map<IsoDate, string> {
  const map = new Map<IsoDate, string>();
  for (const year of years) for (const h of holidaysForYear(year, state)) map.set(h.date, h.name);
  return map;
}

export function holidayName(date: IsoDate, state: FederalState = "NI"): string | undefined {
  return holidayMap([Number(date.slice(0, 4))], state).get(date);
}

export function isHoliday(date: IsoDate, state: FederalState = "NI"): boolean {
  return holidayName(date, state) !== undefined;
}
